import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./my-tooltip.styles.scss";
import cn from "../../../Utils/class-names.utils";

/**
 * 
 * @param {*} [props] 
 * @param {React.Component} [props.children]
 * @param {function} [props.renderTooltip]
 * @param {function} [props.onClose]
 * @param {number} [props.delay]
 * @param {boolean} [props.isFixed]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.hideOnMouseLeave]
 * @param {boolean} [props.disableOnClickOutside]
 * @param {boolean} [props.show]
 * @param {string} [props.className]
 * @param {string} [props.anchorClassName]
 * @param {object} [props.style]
 * @param {number} [props.offset]
 * @param {string} [props.direction]
 * @returns 
 */
const MyTooltip = ({ 
  children, 
  renderTooltip = () => {},
  onClose = () => {},
  delay = 300, 
  isFixed = false,
  disabled = false,
  hideOnMouseLeave = true,
  disableOnClickOutside = true,
  show = false,
  className = '',
  anchorClassName = '',
  style = {},
  offset = 8,
  direction = 'bottom',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const hideOnMouseLeaveRef = useRef(hideOnMouseLeave);

  useEffect(() => {
    onCloseRef.current = onClose;
    hideOnMouseLeaveRef.current = hideOnMouseLeave;
  }, [onClose, hideOnMouseLeave]);

  useEffect(() => {
    if (disabled) return;
    if (isHovered && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
      const tooltipWidth = tooltipRef.current?.offsetWidth || 0;
      
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      let top = 0;
      let left = 0;
      
      switch (direction) {
        case 'top':
          top = rect.top + scrollY - tooltipHeight - offset;
          left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
          
          if (left < scrollX) {
            left = scrollX + offset;
          } else if (left + tooltipWidth > viewportWidth + scrollX) {
            left = viewportWidth + scrollX - tooltipWidth - offset;
          }
          
          if (top < scrollY) {
            top = rect.bottom + scrollY + offset;
          }
          break;
          
        case 'bottom':
          top = rect.bottom + scrollY + offset;
          left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
          
          if (left < scrollX) {
            left = scrollX + offset;
          } else if (left + tooltipWidth > viewportWidth + scrollX) {
            left = viewportWidth + scrollX - tooltipWidth - offset;
          }
          
          if (top + tooltipHeight > viewportHeight + scrollY) {
            top = rect.top + scrollY - tooltipHeight - offset;
          }
          break;
          
        case 'left':
          left = rect.left + scrollX - tooltipWidth - offset;
          top = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
          
          if (top < scrollY) {
            top = scrollY + offset;
          } else if (top + tooltipHeight > viewportHeight + scrollY) {
            top = viewportHeight + scrollY - tooltipHeight - offset;
          }
          
          if (left < scrollX) {
            left = rect.right + scrollX + offset;
          }
          break;
          
        case 'right':
          left = rect.right + scrollX + offset;
          top = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
          
          if (top < scrollY) {
            top = scrollY + offset;
          } else if (top + tooltipHeight > viewportHeight + scrollY) {
            top = viewportHeight + scrollY - tooltipHeight - offset;
          }
          
          if (left + tooltipWidth > viewportWidth + scrollX) {
            left = rect.left + scrollX - tooltipWidth - offset;
          }
          break;
          
        default:
          top = rect.bottom + scrollY + offset;
          left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
      }
      
      setMenuPosition({ top, left });
      hoverTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, delay);
    } else {
      clearTimeout(hoverTimeoutRef.current);
      if (hideOnMouseLeaveRef.current) {
        setShowTooltip(false);
        onCloseRef.current();
      }
    }
  }, [isHovered, disabled, delay, direction, offset]);

  useEffect(() => {
    if (!showTooltip) {
      setIsHovered(false);
    }
  }, [showTooltip]);

  useEffect(() => {
    setShowTooltip(show);
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showTooltip &&
        disableOnClickOutside &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target)
      ) {
        setShowTooltip(false);
        setIsHovered(false);
        onCloseRef.current();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip, disableOnClickOutside]);

  return (
    <div 
      className={`my-tooltip ${className}`} 
      onMouseLeave={() => setIsHovered(false)}
      style={style}
    >
      <div 
        className={cn('d-flex align-items-center anchor', anchorClassName)}
        onMouseEnter={() => setIsHovered(true)}
        ref={anchorRef}
      >
        {children}
      </div>
      {!disabled && (
        <>
          {isFixed ? (
            createPortal(
              <div 
                ref={tooltipRef}
                className={`tooltip-body hide-scrollbar ${showTooltip ? 'show' : ''}`}
                style={{ 
                  position: 'fixed',
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`
                }}
              >
                {renderTooltip({ showTooltip, delay, setShowTooltip, onClose })}
              </div>,
              document.body
            )
          ) : (
            <div 
              ref={tooltipRef}
              className={`tooltip-body hide-scrollbar ${showTooltip ? 'show' : ''}`}
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0
              }}
            >
              {renderTooltip({ showTooltip, delay, setShowTooltip, onClose })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MyTooltip;
